/*
 * Make the cookie banner dismissable when Finsweet's fs-cc.js never loads.
 *
 * THE BUG THIS FIXES
 * The banner is visible by DEFAULT — its `display: block` comes from the site CSS, not from JS.
 * fs-cc.js owns both hiding it (when consent already exists) and dismissing it (on click). So when
 * that one third-party file from cdn.jsdelivr.net does not load, the banner is stuck on screen
 * permanently and no click can remove it. Other click handlers still fire, which is why it looks like
 * the click "works" in the console while nothing happens.
 *
 * Reproduced by blocking only `@finsweet/cookie-consent`; blocking the digital-sparks boost script,
 * GTM, or the first-party gateway does not do it. Identical on the pre-cutover Webflow origin, so this
 * is long-standing behaviour and not a migration regression — but it traps any visitor running an ad
 * blocker or a strict tracking-protection list behind a banner they cannot close.
 *
 * WHAT THIS DOES, AND WHAT IT REFUSES TO DO
 * It makes the banner dismissable and remembers the dismissal. It does NOT grant consent.
 *
 * Consent stays at the opt-in default (`analytics_storage: denied`, verified byte-identical to
 * production). Recording a grant would mean reproducing fs-cc's cookie payload and its GTM contract by
 * hand, and consent is the one surface where guessing wrong has legal consequences. So a visitor who
 * clicks "Accept all" while fs-cc is blocked has their click honoured as a DISMISSAL, not as consent.
 * That is the conservative direction, and a visitor blocking the consent script is almost certainly
 * blocking GTM and analytics as well — there is nothing to grant.
 *
 * The notice stays visible and dismissable rather than being auto-hidden, so a required disclosure is
 * not silently suppressed.
 *
 * WHY TWO DETECTORS
 * A blocked script fires a capture-phase `error` event — measured at 377ms. But engine.js loads in the
 * footer and fs-cc is `async` in the head, so the error can fire BEFORE this code runs and be missed.
 * The polling deadline covers that case. Whichever notices first wins; the worst case is ~2.5s, against
 * a healthy path where the banner is hidden in ~100ms and a broken path that currently never recovers.
 *
 * ZERO EFFECT WHEN fs-cc WORKS. Every path checks `window.FsCC` first and returns if it exists, and the
 * click handler re-checks at click time in case a slow network delivered the script late.
 */

const BANNER = '[fs-cc="banner"]';
/* `allow` is "Accept all", `deny` is the X. Both are <a href="#"> that fs-cc would normally intercept. */
const DISMISS = '[fs-cc="allow"], [fs-cc="deny"]';
/*
 * First-party, no consent required: it stores nothing about the visitor, only that they closed a
 * notice. Without it the banner returns on every navigation, because its visible state is CSS-driven
 * and there is no fs-cc to hide it.
 */
const COOKIE = 'rise-cc-dismissed';
const DEADLINE_MS = 2500;
const SIX_MONTHS = 60 * 60 * 24 * 180;

const fsccReady = () => typeof (window as unknown as { FsCC?: unknown }).FsCC !== 'undefined';

const dismissed = () =>
  document.cookie.split('; ').some((c) => c.startsWith(`${COOKIE}=`));

function remember() {
  document.cookie = `${COOKIE}=1; path=/; max-age=${SIX_MONTHS}; SameSite=Lax`;
}

function hideBanner() {
  for (const el of document.querySelectorAll<HTMLElement>(BANNER)) {
    el.style.display = 'none';
  }
}

let engaged = false;

function engage() {
  /* fs-cc arrived after all — it owns the banner from here. */
  if (engaged || fsccReady()) return;
  engaged = true;

  if (dismissed()) {
    hideBanner();
    return;
  }

  for (const el of document.querySelectorAll<HTMLElement>(DISMISS)) {
    el.addEventListener('click', (ev) => {
      /* A late-loading fs-cc must win: it can record real consent and we cannot. */
      if (fsccReady()) return;
      ev.preventDefault();
      remember();
      hideBanner();
    });
  }
}

function start() {
  if (fsccReady()) return;

  /* Catches the failure the instant the request is refused, when we are early enough to see it. */
  window.addEventListener(
    'error',
    (ev) => {
      const t = ev.target as HTMLScriptElement | null;
      if (t?.tagName === 'SCRIPT' && /cookie-consent|fs-cc/.test(t.src || '')) engage();
    },
    true,
  );

  /* Covers the case where the error already fired before this module ran, and the case where the
   * script loaded but never initialised. */
  window.setTimeout(engage, DEADLINE_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
